import { ref } from 'vue';
import { executeApiRequest } from '~/utils/api';

// Create shared state across components
const userDetails = ref(null);
const userOrders = ref([]);
const userOrder = ref(null);
const userAddresses = ref([]);
const userExercises = ref([]);
const userWorkouts = ref([]);

const userOrdersPage = ref(0);
const userOrdersHasMore = ref(false);
const userOrdersTotal = ref(null);
const userOrdersError = ref(false);
const userOrdersLoaded = ref(false);

/**
 * Composable for fetching and managing user data
 * This replaces direct calls to $auth API functions
 */
export const useUserData = () => {
  /**
   * Helper function to update blackrollCustomer nested properties in userDetails
   */
  const updateBlackrollCustomerProperty = (propertyName, data) => {
    if (userDetails.value && userDetails.value.blackrollCustomer) {
      userDetails.value.blackrollCustomer[propertyName] = data;
    }
  };

  // Get user profile details
  const fetchUserDetails = async () => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/details');
      userDetails.value = data.data;
      return data.data;
    }, 'Failed to fetch user details');
  };

  // Loads the first page by default; pass append to add the next one.
  const fetchUserOrders = async ({ append = false } = {}) => {
    userOrdersError.value = false;

    try {
      return await executeApiRequest(async () => {
        const page = append ? userOrdersPage.value + 1 : 0;
        const data = await $fetch('/api/user/orders', { query: { page } });
        const orders = data.data || [];

        userOrders.value = append ? [...userOrders.value, ...orders] : orders;
        userOrdersPage.value = data.page ?? page;
        userOrdersLoaded.value = true;

        userOrdersTotal.value = data.total ?? userOrdersTotal.value;
        userOrdersHasMore.value = data.hasMore === true;

        return orders;
      }, 'Failed to fetch orders');
    } catch (error) {
      userOrdersError.value = true;
      throw error;
    }
  };

  // Reports failure through userOrdersError rather than by throwing, so callers driving
  // a button do not have to handle a rejection they cannot act on.
  const loadMoreUserOrders = async () => {
    if (!userOrdersHasMore.value) {
      return [];
    }

    return await fetchUserOrders({ append: true }).catch(() => []);
  };

  // Get a single order by number
  const fetchUserOrder = async (orderNumber) => {
    if (!orderNumber) {
      throw new Error('Order number is required');
    }

    return await executeApiRequest(async () => {
      const data = await $fetch(`/api/user/order/${encodeURIComponent(orderNumber)}`);
      userOrder.value = data.data;
      return data.data;
    }, 'Failed to fetch order details');
  };

  // Get user addresses
  const fetchUserAddresses = async () => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/addresses');
      userAddresses.value = data.data || [];
      return data.data;
    }, 'Failed to fetch addresses');
  };

  // Update user profile
  const updateUserProfile = async (profileData) => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/profile', {
        method: 'PUT',
        body: profileData,
      });
      userDetails.value = data.data;
      return data.data;
    }, 'Failed to update profile');
  };

  // Add/update address
  const updateAddress = async (addressData, addressIndex = null) => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/address', {
        method: 'PUT',
        body: { address: addressData, index: addressIndex },
      });
      userAddresses.value = data.data || [];
      return data.data;
    }, 'Failed to update address');
  };

  // Delete address
  const deleteAddress = async (addressIndex) => {
    return await executeApiRequest(async () => {
      const data = await $fetch(`/api/user/address/${addressIndex}`, {
        method: 'DELETE',
      });
      userAddresses.value = data.data || [];
      return data.data;
    }, 'Failed to delete address');
  };

  // Get user exercises
  const getUserExercises = async () => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/exercises');
      userExercises.value = data.data || [];
      return data.data;
    }, 'Failed to fetch exercises');
  };

  // Get user workouts
  const getUserWorkouts = async () => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/workouts');
      userWorkouts.value = data.data || [];
      return data.data;
    }, 'Failed to fetch workouts');
  };

  // Add an exercise
  const addExercise = async (exerciseKey, pimExerciseId) => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/exercise', {
        method: 'POST',
        body: { exerciseKey, pimExerciseId },
      });

      // Update the exercises in the shared state
      userExercises.value = data.data || [];

      // Update exercises in userDetails if available
      updateBlackrollCustomerProperty('exercises', data.data);

      return data.data;
    }, 'Failed to add exercise');
  };

  // Delete an exercise
  const deleteExercise = async (exerciseKey, pimExerciseId) => {
    return await executeApiRequest(async () => {
      const data = await $fetch(`/api/user/exercise/${exerciseKey}`, {
        method: 'DELETE',
        body: { pimExerciseId },
      });

      // Update the exercises in the shared state
      userExercises.value = data.data || [];

      // Update exercises in userDetails if available
      updateBlackrollCustomerProperty('exercises', data.data);

      return data.data;
    }, 'Failed to delete exercise');
  };

  // Add a workout
  const addWorkout = async (routineObjectId, pimId) => {
    return await executeApiRequest(async () => {
      const data = await $fetch('/api/user/workout', {
        method: 'POST',
        body: { routineObjectId, pimId },
      });

      // Update the workouts in the shared state
      userWorkouts.value = data.data || [];

      // Update workouts in userDetails if available
      updateBlackrollCustomerProperty('workouts', data.data);

      return data.data;
    }, 'Failed to add workout');
  };

  // Delete a workout
  const deleteWorkout = async (routineObjectId, pimId) => {
    return await executeApiRequest(async () => {
      const data = await $fetch(`/api/user/workout/${routineObjectId}`, {
        method: 'DELETE',
        body: { pimId },
      });

      // Update the workouts in the shared state
      userWorkouts.value = data.data || [];

      // Update workouts in userDetails if available
      updateBlackrollCustomerProperty('workouts', data.data);

      return data.data;
    }, 'Failed to delete workout');
  };

  // Clear all user data (e.g., on logout)
  const clearUserData = () => {
    userDetails.value = null;
    userOrders.value = [];
    userOrder.value = [];
    userAddresses.value = [];
    userExercises.value = [];
    userWorkouts.value = [];
    userOrdersPage.value = 0;
    userOrdersHasMore.value = false;
    userOrdersLoaded.value = false;
    userOrdersTotal.value = null;
    userOrdersError.value = false;
  };

  return {
    // State
    userDetails,
    userOrders,
    userOrder,
    userAddresses,
    userExercises,
    userWorkouts,
    userOrdersHasMore,
    userOrdersLoaded,
    userOrdersTotal,
    userOrdersError,

    // Methods
    fetchUserDetails,
    fetchUserOrders,
    loadMoreUserOrders,
    fetchUserOrder,
    fetchUserAddresses,
    updateUserProfile,
    updateAddress,
    deleteAddress,
    getUserExercises,
    getUserWorkouts,
    addExercise,
    deleteExercise,
    addWorkout,
    deleteWorkout,
    clearUserData,
  };
};
